import React from 'react';
import { useAppConfiguration } from '@/hooks/useAppConfiguration';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Loader2 } from 'lucide-react';

interface ProgramariRouteGuardProps {
  children: React.ReactNode;
}

/**
 * Component that guards the /appointments route
 * 
 * This guard allows /appointments to work as a standalone route.
 * The Programari component will handle the display logic based on configuration.
 * 
 * Employee Usage by Plan and Appointment Type:
 * - STARTER plan (website_presentation): Employees NOT used for appointments
 *   Only accessible for blog assignment and team cards
 * 
 * - PRO/ENTERPRISE with lodging_days: Employees NOT used for appointments
 *   Only accessible for blog assignment and team cards
 * 
 * - PRO/ENTERPRISE with clinic_hours: Employees ARE used for appointments
 *   Only employees with clinic_hours services are shown for appointment booking
 */
export default function ProgramariRouteGuard({ children }: ProgramariRouteGuardProps) {
  const { isLoading } = useAppConfiguration();
  const { isLoading: subscriptionLoading } = useSubscription();

  // Show loading while checking configuration
  if (isLoading || subscriptionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Allow access to /appointments route regardless of appointmentType
  // The Programari component will handle the display logic based on configuration
  // This makes /appointments work as a standalone route
  return <>{children}</>;
}


